import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import {
  fetchAgentInAppPopupsUnseen,
  markAgentInAppPopupNeverAgain,
  markAgentInAppPopupSeen,
  type InAppPopupRow,
} from '../services/api';
import { AGENT_AUTH_TOKEN_CHANGED_EVENT } from '../constants';
import BroadcastMessageBody from './BroadcastMessageBody';

/**
 * Same behavior as kiosk in-app gate: once = OK marks seen; sticky_daily = OK until next load / reload;
 * no Back/Next; refetch when agent auth changes.
 */
export default function AgentInAppPopupGate() {
  const { timezone } = useTimezone();
  const [queue, setQueue] = useState<InAppPopupRow[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [marking, setMarking] = useState(false);
  const batchTotalRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const res = await fetchAgentInAppPopupsUnseen();
      if (res.success && res.data?.popups?.length) {
        const list = res.data.popups;
        batchTotalRef.current = list.length;
        setQueue(list);
        setActiveIndex(0);
      } else {
        batchTotalRef.current = 0;
        setQueue([]);
        setActiveIndex(0);
      }
    } catch {
      batchTotalRef.current = 0;
      setQueue([]);
      setActiveIndex(0);
    }
  }, []);

  useEffect(() => {
    void load();
    const onAuth = () => void load();
    window.addEventListener(AGENT_AUTH_TOKEN_CHANGED_EVENT, onAuth);
    return () => window.removeEventListener(AGENT_AUTH_TOKEN_CHANGED_EVENT, onAuth);
  }, [load]);

  const current = queue[activeIndex];
  const isSticky = current?.displayMode === 'sticky_daily';

  const removeCurrentFromQueue = () => {
    setQueue((prev) => {
      const next = prev.filter((_, i) => i !== activeIndex);
      setActiveIndex((idx) => {
        if (next.length === 0) return 0;
        return Math.min(idx, next.length - 1);
      });
      return next;
    });
  };

  const onMarkSeen = async () => {
    if (!current || marking || isSticky) return;
    setMarking(true);
    try {
      const res = await markAgentInAppPopupSeen(current.id);
      if (res.success) removeCurrentFromQueue();
    } finally {
      setMarking(false);
    }
  };

  const onCloseSticky = () => {
    if (!current || marking || !isSticky) return;
    removeCurrentFromQueue();
  };

  const onNeverAgain = async () => {
    if (!current || marking || !isSticky) return;
    setMarking(true);
    try {
      const res = await markAgentInAppPopupNeverAgain(current.id);
      if (res.success) removeCurrentFromQueue();
    } finally {
      setMarking(false);
    }
  };

  if (!current) return null;

  const batchTotal = batchTotalRef.current;
  const positionInBatch = activeIndex + 1;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/48 p-4 backdrop-blur-[1.5px]" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-in-app-popup-title"
        aria-describedby={batchTotal > 1 ? 'agent-in-app-popup-step' : undefined}
        className="flex max-h-[85vh] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_18px_40px_rgba(15,23,42,0.18)]"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-7 py-6">
          <h2 id="agent-in-app-popup-title" className="min-w-0 flex-1 truncate text-2xl font-semibold leading-8 text-slate-900">
            {current.title || 'Announcement'}
          </h2>
          {batchTotal > 1 ? (
            <p id="agent-in-app-popup-step" className="shrink-0 rounded-md bg-slate-100 px-2.5 py-1 text-[10px] font-medium tabular-nums text-slate-600">
              {positionInBatch} of {batchTotal}
            </p>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-7 py-5 text-base leading-7 text-slate-700">
          {isSticky && current.expiresAt ? (
            <p className="mb-3 text-xs text-slate-400">
              Ends{' '}
              {formatInTimezone(current.expiresAt, timezone)}
            </p>
          ) : null}
          <div className="whitespace-pre-wrap">
            <BroadcastMessageBody text={current.body} linkClassName="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700 break-all" />
          </div>
        </div>

        <div className="px-7 pb-6 pt-4">
          {isSticky ? (
            <>
              <button
                type="button"
                onClick={() => onCloseSticky()}
                disabled={marking}
                className="mx-auto block w-1/3 max-w-full rounded-xl bg-slate-900 px-4 py-3.5 text-base font-semibold text-white transition duration-150 hover:bg-slate-800 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
              >
                OK
              </button>
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => void onNeverAgain()}
                  disabled={marking}
                  className="text-[11px] text-slate-400 underline decoration-slate-300 underline-offset-2 transition duration-150 hover:text-slate-500 disabled:opacity-40"
                >
                  Don&apos;t show this again
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => void onMarkSeen()}
              disabled={marking}
              className="mx-auto block w-1/3 max-w-full rounded-xl bg-slate-900 px-4 py-3.5 text-base font-semibold text-white transition duration-150 hover:bg-slate-800 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {marking ? 'Saving...' : 'OK'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
